import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Clock,
  Search,
  User,
  Phone,
  Banknote,
  RefreshCw,
  X,
  Package,
  ChevronRight,
  Trash2,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PendingOrdersPanel = ({ onSelectOrder, onClose }) => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrders();
    // Auto-refresh every 5 seconds
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [search]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/temporary-orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "pending", search },
      });
      setOrders(res.data.filter((order) => order.source !== "customer_store"));

      const poRes = await axios.get(`${API_URL}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "Pending Approval" },
      });
      let pos = poRes.data.data || [];
      if (search) {
        pos = pos.filter(po => 
          (po.supplier_name && po.supplier_name.toLowerCase().includes(search.toLowerCase())) ||
          (po.po_number && po.po_number.toLowerCase().includes(search.toLowerCase()))
        );
      }
      setPurchaseOrders(pos);
    } catch (err) {
      console.error("Failed to fetch pending orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrder = (order) => {
    onSelectOrder(order);
    onClose();
  };

  const handleDeleteOrder = async (orderId, e) => {
    e.stopPropagation(); // Prevent triggering the order selection

    if (
      !window.confirm("Are you sure you want to delete this pending order?")
    ) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/temporary-orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Pending order deleted");
      fetchOrders(); // Refresh the list
    } catch (err) {
      console.error("Failed to delete order:", err);
      toast.error("Failed to delete order");
    }
  };

  const handleApproveOrder = async (orderId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API_URL}/temporary-orders/${orderId}`, { status: "approved" }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Customer order approved. Payment is now available to the customer.");
      fetchOrders();
    } catch (err) {
      console.error("Failed to approve customer order:", err);
      toast.error(err.response?.data?.detail || "Failed to approve order");
    }
  };

  const handleApprovePO = async (orderId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API_URL}/purchase-orders/${orderId}`, { status: "Pending" }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Purchase Order approved.");
      fetchOrders();
    } catch (err) {
      console.error("Failed to approve PO:", err);
      toast.error("Failed to approve Purchase Order");
    }
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 25 }}
      className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="flex items-center gap-2">
          <Clock size={20} />
          <h2 className="font-bold text-lg">Pending Orders</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b bg-stone-50">
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, or order #"
            className="pl-10 pr-10"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-stone-500">
            {orders.length + purchaseOrders.length} pending request{orders.length + purchaseOrders.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={fetchOrders}
            className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Orders List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <AnimatePresence>
          {orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Package size={48} className="text-stone-300 mb-3" />
              <p className="text-stone-400 text-sm">No pending orders</p>
            </div>
          ) : (
            orders.map((order) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                onClick={() => handleSelectOrder(order)}
                className="bg-white border-2 border-stone-200 hover:border-amber-400 rounded-xl p-4 cursor-pointer transition-all group"
              >
                <div
                  onClick={() => handleSelectOrder(order)}
                  className="flex items-start justify-between mb-3"
                >
                  <div className="flex-1">
                    <p className="font-bold text-sm text-stone-800 flex items-center gap-2">
                      {order.temp_order_number}
                      {order.source === "customer_store" && (
                        <span className="text-[9px] uppercase tracking-wide bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                          Customer Store
                        </span>
                      )}
                      <ChevronRight
                        size={14}
                        className="text-stone-400 group-hover:text-amber-500 transition-colors"
                      />
                    </p>
                    <button
                      onClick={(e) => handleDeleteOrder(order.id, e)}
                      className="opacity-1 group-hover:opacity-100 p-1.5 text-black-600 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all"
                      title="Delete order"
                    >
                      <Trash2 fill="black" size={14} />
                    </button>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {new Date(order.created_at).toLocaleString("en-IN", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <span className="text-lg font-mono font-bold text-amber-600">
                    ₹{Number(order.amount_payable ?? order.total ?? order.subtotal ?? 0).toFixed(2)}
                  </span>
                </div>

                <div className="space-y-1.5">
                  {order.customer_name && (
                    <div className="flex items-center gap-2 text-xs text-stone-600">
                      <User size={12} className="text-stone-400" />
                      <span className="font-medium">{order.customer_name}</span>
                    </div>
                  )}
                  {order.customer_phone && (
                    <div className="flex items-center gap-2 text-xs text-stone-600">
                      <Phone size={12} className="text-stone-400" />
                      <span className="font-mono">{order.customer_phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-stone-600">
                    <Package size={12} className="text-stone-400" />
                    <span>
                      {order.items.reduce((s, i) => s + i.quantity, 0)} items
                    </span>
                  </div>
                  {order.amount_received > 0 && (
                    <div className="flex items-center gap-2 text-xs text-emerald-600">
                      <Banknote size={12} />
                      <span className="font-semibold">
                        ₹{order.amount_received.toFixed(2)} received
                      </span>
                    </div>
                  )}
                  {order.referred_by_staff_name && (
                    <div className="flex items-center gap-2 text-xs text-stone-600">
                      <User size={12} className="text-amber-500" />
                      <span className="font-semibold">
                        Ref: {order.referred_by_staff_name}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-stone-100">
                  <div className="flex flex-wrap gap-1">
                    {order.items.slice(0, 3).map((item, i) => (
                      <span
                        key={i}
                        className="text-[10px] bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full"
                      >
                        {item.product_name} ×{item.quantity}
                      </span>
                    ))}
                    {order.items.length > 3 && (
                      <span className="text-[10px] bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full font-semibold">
                        +{order.items.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                {order.source === "customer_store" && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={(e) => handleApproveOrder(order.id, e)}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-600"
                    >
                      <CheckCircle2 size={14} /> Approve Order
                    </button>
                  </div>
                )}
              </motion.div>
            ))
          )}
          
          {purchaseOrders.length > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-200">
              <h3 className="text-sm font-bold text-stone-500 uppercase tracking-wider mb-3">Pending Purchase Orders</h3>
              {purchaseOrders.map((po) => (
                <motion.div
                  key={po.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-blue-50 border-2 border-blue-200 hover:border-blue-400 rounded-xl p-4 cursor-default transition-all mb-3"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-sm text-blue-900">{po.supplier_name}</p>
                      <p className="text-xs text-blue-700 font-mono mt-0.5">{po.po_number}</p>
                    </div>
                    <span className="text-lg font-mono font-bold text-blue-700">
                      ₹{Number(po.total_amount).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-blue-700 mt-2">
                    <User size={12} />
                    <span>Req by: {po.generated_by || "Unknown"}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-blue-700 mt-1">
                    <Package size={12} />
                    <span>{po.items?.reduce((s, i) => s + i.quantity, 0) || 0} items</span>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={(e) => handleApprovePO(po.id, e)}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-700"
                    >
                      <CheckCircle2 size={14} /> Approve PO
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default PendingOrdersPanel;
